import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../utils/Api";
import { selectStorageLocation } from "../store/authSlice";
import ScreenShell from "../components/ScreenShell";
import BottomSheet from "../components/BottomSheet";
import useToast from "../hooks/useToast";
import { formatQuantity, formatUnit, getErrorMessage, toISODate } from "../utils/format";

// ---- API endpoints used on this screen -------------------------------
// GET raw-material/get-storage-location-stock-transfer-report
//       ?startDate&endDate&storageLocationId[&isDownload=true -> .xlsx blob]
//       -> data.data.data[] = { rawMaterialId, rawMaterial, mainRmCategory, subRmCategory,
//            storageLocation, totalCredit, totalDebit, availableStock, unit, status }
// GET raw-material/get-outlet-storage-location-stock-transfer
//       ?rawMaterialId&startDate&endDate
//       -> data.data.{ outletData[], storageLocationData[] }
//       Only storageLocationData is used, filtered to the user's own location.
// -----------------------------------------------------------------------
// storageLocationId is always the logged-in staff's assigned location (Redux).

// Keep in sync with MAX_DATE_RANGE_DAYS in the admin app's constants/dateConstants.
const MAX_RANGE_DAYS = 60;

const STATUS = {
    INSTOCK: { label: "In stock", fg: "#0A6B58", bg: "#E5F7F3", border: "#B9E9DF", dot: "#00A88C" },
    LOWSTOCK: { label: "Low stock", fg: "#B26A00", bg: "#FFF7E6", border: "#FCE4B4", dot: "#E08A3C" },
    OUTOFSTOCK: { label: "Out of stock", fg: "#B42318", bg: "#FEF1F0", border: "#F8C9C5", dot: "#E5484D" },
};
const FALLBACK_STATUS = { label: "Unknown", fg: "#5B6B7C", bg: "#F1FAFD", border: "#DCEAF1", dot: "#8A9BA8" };
const getStatus = (status) => STATUS[status] || { ...FALLBACK_STATUS, label: status || FALLBACK_STATUS.label };

const FILTERS = [
    { key: "ALL", label: "All" },
    { key: "INSTOCK", label: "In stock" },
    { key: "LOWSTOCK", label: "Low" },
    { key: "OUTOFSTOCK", label: "Out" },
];

const StatusPill = ({ status }) => {
    const s = getStatus(status);
    return (
        <span
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold whitespace-nowrap"
            style={{ background: s.bg, borderColor: s.border, color: s.fg }}
        >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
            {s.label}
        </span>
    );
};

const Stat = ({ label, value, unit, color }) => (
    <div className="min-w-0">
        <p className="xl-mono text-[8.5px] font-medium uppercase tracking-wide text-[#8A9BA8]">{label}</p>
        <p className="text-[13px] font-bold mt-0.5 truncate" style={{ color }}>
            {value} <span className="text-[10px] font-semibold opacity-70">{unit}</span>
        </p>
    </div>
);

// ---- Detail sheet: stock of one material at the user's own storage location ----
// The API also returns outlet data and every other storage location; neither is
// shown. Only the entry that belongs to the logged-in staff's location (Redux) is kept.
const normalizeName = (value) => (value || "").toString().trim().toLowerCase();

const belongsToLocation = (entry, location) => {
    if (!location) return false;
    const entryId = entry?.storageLocationId || entry?.storageLocation?._id;
    if (entryId) return entryId === location._id;
    // Fallback when the API only sends the name
    const entryName = typeof entry?.storageLocation === "string" ? entry.storageLocation : entry?.storageLocation?.name;
    return !!entryName && normalizeName(entryName) === normalizeName(location.name);
};

const DetailSheet = ({ row, startDate, endDate, onClose }) => {
    const storageLocation = useSelector(selectStorageLocation);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [entries, setEntries] = useState([]);
    const requestRef = useRef(0);

    const rawMaterialId = row?.rawMaterialId;

    useEffect(() => {
        if (!rawMaterialId) return;
        const requestId = ++requestRef.current;
        setLoading(true);
        setError("");
        setEntries([]);

        axiosInstance
            .get("raw-material/get-outlet-storage-location-stock-transfer", {
                params: { rawMaterialId, startDate, endDate },
            })
            .then((response) => {
                if (requestId !== requestRef.current) return; // a newer request superseded this one
                if (response?.data?.status === true) {
                    const all = response.data.data?.storageLocationData || [];
                    setEntries(all.filter((entry) => belongsToLocation(entry, storageLocation)));
                }
            })
            .catch((err) => {
                if (requestId !== requestRef.current) return;
                setError(getErrorMessage(err, "Failed to fetch stock details."));
            })
            .finally(() => {
                if (requestId === requestRef.current) setLoading(false);
            });
    }, [rawMaterialId, startDate, endDate, storageLocation]);

    return (
        <BottomSheet
            open={!!row}
            onClose={onClose}
            title={row?.rawMaterial || "Stock details"}
            subtitle={[row?.mainRmCategory, row?.subRmCategory].filter(Boolean).join("  ›  ")}
        >
            {loading ? (
                <div className="space-y-3" aria-busy="true">
                    <div className="skeleton h-28 rounded-[18px]" />
                </div>
            ) : error ? (
                <p className="rounded-[16px] bg-[#FEF1F0] border border-[#F8C9C5] px-4 py-3 text-[13px] font-semibold text-[#B42318]">
                    {error}
                </p>
            ) : entries.length === 0 ? (
                <p className="rounded-[18px] border border-dashed border-[#C9DCE5] bg-white/60 px-5 py-8 text-center text-[13px] font-semibold text-[#5B6B7C]">
                    No stock data found for {storageLocation?.name || "your storage location"}
                </p>
            ) : (
                <ul className="space-y-3">
                    {entries.map((entry, index) => {
                        const unit = formatUnit(entry.unit);
                        return (
                            <li
                                key={entry.storageLocationId || `location-${index}`}
                                className="rounded-[18px] bg-white border border-[#DCEAF1] p-3.5"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <p className="text-[14px] font-bold text-[#08213D] leading-snug min-w-0">
                                        {storageLocation?.name || entry.storageLocation}
                                    </p>
                                    <StatusPill status={entry.status} />
                                </div>
                                <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 mt-3">
                                    <Stat label="Credited" value={`+${formatQuantity(entry.totalCredit)}`} unit={unit} color="#0A6B58" />
                                    <Stat label="Debited" value={`−${formatQuantity(entry.totalDebit)}`} unit={unit} color="#B42318" />
                                    <Stat label="Available" value={formatQuantity(entry.availableStock)} unit={unit} color="#08213D" />
                                    <Stat label="Re-order level" value={formatQuantity(entry.reOrderLevel)} unit={unit} color="#5B6B7C" />
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </BottomSheet>
    );
};

const StockReport = () => {
    const storageLocation = useSelector(selectStorageLocation);
    const storageLocationId = storageLocation?._id;
    const { toast, showToast, dismissToast } = useToast();

    const today = useMemo(() => toISODate(new Date()), []);
    const monthStart = useMemo(() => {
        const now = new Date();
        return toISODate(new Date(now.getFullYear(), now.getMonth(), 1));
    }, []);

    // Inputs vs. what the loaded report was generated for
    const [startDate, setStartDate] = useState(monthStart);
    const [endDate, setEndDate] = useState(today);
    const [applied, setApplied] = useState({ start: monthStart, end: today });

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [downloading, setDownloading] = useState(false);

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [detailRow, setDetailRow] = useState(null);
    const requestRef = useRef(0);

    const rangeError = useMemo(() => {
        if (!startDate || !endDate) return "Select both dates.";
        if (endDate < startDate) return "End date can't be before the start date.";
        const days = (new Date(endDate) - new Date(startDate)) / 86400000;
        if (days > MAX_RANGE_DAYS) return `Date range can't exceed ${MAX_RANGE_DAYS} days.`;
        return "";
    }, [startDate, endDate]);

    const fetchReport = useCallback(
        async (start, end) => {
            if (!storageLocationId) {
                setLoading(false);
                return;
            }
            const requestId = ++requestRef.current;
            try {
                setLoading(true);
                setError("");
                const response = await axiosInstance.get("raw-material/get-storage-location-stock-transfer-report", {
                    params: { startDate: start, endDate: end, storageLocationId },
                });
                if (requestId !== requestRef.current) return;
                setRows(response?.data?.data?.data || []);
            } catch (err) {
                if (requestId !== requestRef.current) return;
                console.log("Stock report error:", err?.response);
                setRows([]);
                setError(getErrorMessage(err, "Failed to load the stock report."));
            } finally {
                if (requestId === requestRef.current) setLoading(false);
            }
        },
        [storageLocationId]
    );

    // Initial load: this month to date
    useEffect(() => {
        fetchReport(applied.start, applied.end);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchReport]);

    const handleApply = () => {
        if (rangeError) return showToast("error", rangeError);
        setApplied({ start: startDate, end: endDate });
        fetchReport(startDate, endDate);
    };

    const handleDownload = async () => {
        if (downloading || !rows.length) return;
        try {
            setDownloading(true);
            const response = await axiosInstance.get("raw-material/get-storage-location-stock-transfer-report", {
                params: {
                    startDate: applied.start,
                    endDate: applied.end,
                    storageLocationId,
                    isDownload: true,
                },
                responseType: "blob",
            });
            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${storageLocation?.name || "Storage location"} Stock Report (${applied.start} - ${applied.end}).xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            showToast("success", "Excel file downloaded.");
        } catch (err) {
            console.log("Stock report download error:", err?.response);
            showToast("error", getErrorMessage(err, "Could not download the Excel file."));
        } finally {
            setDownloading(false);
        }
    };

    const counts = useMemo(() => {
        const base = { ALL: rows.length, INSTOCK: 0, LOWSTOCK: 0, OUTOFSTOCK: 0 };
        rows.forEach((row) => {
            if (base[row.status] !== undefined) base[row.status] += 1;
        });
        return base;
    }, [rows]);

    const visibleRows = useMemo(() => {
        const term = search.trim().toLowerCase();
        return rows.filter((row) => {
            if (statusFilter !== "ALL" && row.status !== statusFilter) return false;
            if (!term) return true;
            return [row.rawMaterial, row.mainRmCategory, row.subRmCategory]
                .filter(Boolean)
                .some((text) => text.toLowerCase().includes(term));
        });
    }, [rows, search, statusFilter]);

    const rangeLabel = (iso) =>
        new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

    return (
        <ScreenShell
            title="Stock report"
            subtitle="Credits, debits and available stock"
            toast={toast}
            onDismissToast={dismissToast}
        >
            <div className="px-5 pt-5 pb-10 space-y-4">
                {!storageLocationId ? (
                    <div className="rounded-[20px] bg-white border border-[#DCEAF1] px-5 py-8 text-center">
                        <p className="xl-display text-[16px] font-bold text-[#08213D]">No storage location assigned</p>
                        <p className="text-[12px] font-medium text-[#5B6B7C] mt-1">
                            Ask your admin to assign a storage location to your account.
                        </p>
                    </div>
                ) : (
                    <>
                        {/* ---- Date range ---- */}
                        <div className="rounded-[20px] bg-white border border-[#DCEAF1] p-4 shadow-[0_8px_24px_rgba(4,33,63,0.05)]">
                            <div className="grid grid-cols-2 gap-3">
                                {[
                                    { id: "report-start", label: "From", value: startDate, set: setStartDate },
                                    { id: "report-end", label: "To", value: endDate, set: setEndDate },
                                ].map((field) => (
                                    <div key={field.id}>
                                        <label htmlFor={field.id} className="block text-[12px] font-bold text-[#08213D] mb-1.5">
                                            {field.label}
                                        </label>
                                        <input
                                            id={field.id}
                                            type="date"
                                            value={field.value}
                                            max={today}
                                            onChange={(event) => field.set(event.target.value)}
                                            className="w-full h-12 rounded-[14px] border border-[#DCEAF1] bg-[#F9FCFD] px-3 text-[14px] font-semibold text-[#08213D] focus:outline-none focus:ring-2 focus:ring-[#00A4E3]/30 focus:border-[#00A4E3]"
                                        />
                                    </div>
                                ))}
                            </div>
                            {rangeError && <p className="text-[12px] font-semibold text-[#D92D20] mt-2">{rangeError}</p>}

                            <div className="flex gap-3 mt-4">
                                <button
                                    type="button"
                                    onClick={handleApply}
                                    disabled={loading}
                                    className="flex-1 h-12 rounded-[14px] bg-[#04698F] text-white text-[13px] font-bold shadow-[0_10px_22px_rgba(4,105,143,0.25)] active:scale-[0.98] transition disabled:opacity-70 flex items-center justify-center gap-2"
                                >
                                    {loading && <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />}
                                    Generate report
                                </button>
                                <button
                                    type="button"
                                    onClick={handleDownload}
                                    disabled={!rows.length || downloading}
                                    aria-label="Download Excel"
                                    className="h-12 px-4 rounded-[14px] bg-white border border-[#B9E9DF] text-[#0A6B58] text-[13px] font-bold active:scale-[0.98] transition disabled:opacity-40 flex items-center gap-2"
                                >
                                    {downloading ? (
                                        <span className="w-4 h-4 rounded-full border-2 border-[#B9E9DF] border-t-[#0A6B58] animate-spin" />
                                    ) : (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 3v12M7 11l5 5 5-5M4 21h16" />
                                        </svg>
                                    )}
                                    Excel
                                </button>
                            </div>
                        </div>

                        {/* ---- Status tiles (double as filters) ---- */}
                        <div className="grid grid-cols-4 gap-2">
                            {FILTERS.map((filter) => {
                                const isActive = statusFilter === filter.key;
                                const dot = filter.key === "ALL" ? "#04698F" : STATUS[filter.key].dot;
                                return (
                                    <button
                                        key={filter.key}
                                        type="button"
                                        onClick={() => setStatusFilter(filter.key)}
                                        aria-pressed={isActive}
                                        className={`rounded-[16px] border px-2 py-2.5 text-center transition active:scale-95 ${
                                            isActive ? "bg-white border-[#04698F] shadow-[0_6px_16px_rgba(4,105,143,0.15)]" : "bg-white/70 border-[#DCEAF1]"
                                        }`}
                                    >
                                        <p className="xl-display text-[18px] font-bold leading-none" style={{ color: dot }}>
                                            {counts[filter.key]}
                                        </p>
                                        <p className="text-[10px] font-bold text-[#5B6B7C] mt-1">{filter.label}</p>
                                    </button>
                                );
                            })}
                        </div>

                        {/* ---- Search ---- */}
                        <div className="relative">
                            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8A9BA8]" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                                <circle cx="11" cy="11" r="7" />
                                <path d="m20 20-3.5-3.5" />
                            </svg>
                            <input
                                type="search"
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Search material or category"
                                aria-label="Search stock report"
                                className="w-full h-12 rounded-[14px] border border-[#DCEAF1] bg-white pl-10 pr-3 text-[16px] font-medium text-[#08213D] focus:outline-none focus:ring-2 focus:ring-[#00A4E3]/30 focus:border-[#00A4E3]"
                            />
                        </div>

                        <p className="xl-mono text-[9px] font-medium uppercase tracking-wide text-[#5B6B7C] px-1">
                            {rangeLabel(applied.start)} – {rangeLabel(applied.end)}
                            {!loading && !error && ` · ${visibleRows.length} of ${rows.length} items`}
                        </p>

                        {/* ---- Results ---- */}
                        {loading ? (
                            <div className="space-y-3" aria-busy="true">
                                {[0, 1, 2, 3].map((n) => (
                                    <div key={n} className="skeleton h-[132px] rounded-[20px]" />
                                ))}
                            </div>
                        ) : error ? (
                            <div className="rounded-[20px] bg-[#FEF1F0] border border-[#F8C9C5] px-5 py-6 text-center">
                                <p className="text-[13px] font-semibold text-[#B42318]">{error}</p>
                                <button
                                    type="button"
                                    onClick={() => fetchReport(applied.start, applied.end)}
                                    className="mt-3 h-10 px-5 rounded-[12px] bg-white border border-[#F8C9C5] text-[12px] font-bold text-[#B42318] active:scale-95 transition"
                                >
                                    Try again
                                </button>
                            </div>
                        ) : visibleRows.length === 0 ? (
                            <div className="rounded-[20px] border border-dashed border-[#C9DCE5] bg-white/60 px-5 py-10 text-center">
                                <p className="text-[13px] font-semibold text-[#5B6B7C]">
                                    {rows.length === 0 ? "No stock movement found for this period" : "Nothing matches your filters"}
                                </p>
                            </div>
                        ) : (
                            <ul className="space-y-3">
                                {visibleRows.map((row, index) => {
                                    const unit = formatUnit(row.unit);
                                    return (
                                        <li key={`${row.rawMaterialId || row.rawMaterial}-${index}`}>
                                            <button
                                                type="button"
                                                onClick={() => setDetailRow(row)}
                                                className="w-full text-left rounded-[20px] bg-white border border-[#DCEAF1] p-4 shadow-[0_8px_24px_rgba(4,33,63,0.05)] active:scale-[0.99] transition"
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <p className="text-[14px] font-bold text-[#08213D] leading-snug">{row.rawMaterial}</p>
                                                        <p className="text-[11px] font-semibold text-[#04698F] mt-0.5">
                                                            {[row.mainRmCategory, row.subRmCategory].filter(Boolean).join("  ›  ")}
                                                        </p>
                                                    </div>
                                                    <StatusPill status={row.status} />
                                                </div>

                                                <div className="mt-3 flex items-end justify-between gap-3">
                                                    <div>
                                                        <p className="xl-mono text-[8.5px] font-medium uppercase tracking-wide text-[#8A9BA8]">
                                                            Available
                                                        </p>
                                                        <p className="xl-display text-[24px] font-bold text-[#08213D] leading-tight">
                                                            {formatQuantity(row.availableStock)}{" "}
                                                            <span className="text-[12px] font-semibold text-[#5B6B7C]">{unit}</span>
                                                        </p>
                                                    </div>
                                                    <div className="flex gap-4 pb-1">
                                                        <Stat label="Credited" value={`+${formatQuantity(row.totalCredit)}`} unit={unit} color="#0A6B58" />
                                                        <Stat label="Debited" value={`−${formatQuantity(row.totalDebit)}`} unit={unit} color="#B42318" />
                                                    </div>
                                                </div>

                                                <p className="text-[10px] font-semibold text-[#8A9BA8] mt-2.5 flex items-center gap-1">
                                                    View stock details
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="m9 6 6 6-6 6" />
                                                    </svg>
                                                </p>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </>
                )}
            </div>

            <DetailSheet
                row={detailRow}
                startDate={applied.start}
                endDate={applied.end}
                onClose={() => setDetailRow(null)}
            />
        </ScreenShell>
    );
};

export default StockReport;